import { GreetingMode } from "@/types/campaigns";
import type { GreetingSource } from "@/utils/scripts";

/** Matches `@[Display Name](variable_id)` mention-style variable tokens. */
const MENTION_TOKEN_REGEX = /@\[([^\]]+)\]\(([^)]+)\)/g;
/** Matches `{{token}}` and `{token}` brace-style variable tokens. */
const BRACE_TOKEN_REGEX = /\{\{?([^{}]+)\}?\}/g;

/**
 * Replace variable tokens with something speakable so a TTS preview doesn't
 * read `@[First Name](first_name)` or `{{first_name}}` literally. Mention
 * tokens use their display label; brace tokens use the token name with
 * underscores turned into spaces.
 */
function humanizeVariableTokens(text: string): string {
  return text
    .replace(MENTION_TOKEN_REGEX, (_match, display: string) => display)
    .replace(BRACE_TOKEN_REGEX, (_match, token: string) => token.trim().replace(/_/g, " "))
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}

/**
 * Resolve the single spoken greeting a caller would actually hear, for
 * seeding the voice-preview text. Unlike `getGreetingPreview` (a newline
 * summary for collapsed editors), this returns one speakable string.
 *
 * - "static": the greeting itself.
 * - "random": the first non-empty variant.
 * - "time_specific": the bucket matching the current hour (morning < 12,
 *   afternoon < 18, night otherwise), falling back through the others.
 * - "prompt": null — the greeting is AI-generated, there is no literal text.
 *
 * Returns null when no usable text exists so callers can fall back to a
 * canned sample sentence. Output is truncated to `maxChars`.
 */
export function resolveSpokenGreeting(
  script: GreetingSource,
  options?: { maxChars?: number; now?: Date }
): string | null {
  const mode: GreetingMode = script.greetingMode ?? "static";

  let raw: string | undefined;
  switch (mode) {
    case "prompt":
      return null;
    case "random":
      raw = script.greetingVariants?.find((variant) => variant?.trim());
      break;
    case "time_specific": {
      const hour = (options?.now ?? new Date()).getHours();
      const byTime =
        hour < 12
          ? script.greetingMorning
          : hour < 18
            ? script.greetingAfternoon
            : script.greetingNight;
      raw =
        byTime?.trim() ||
        [script.greetingMorning, script.greetingAfternoon, script.greetingNight].find((g) =>
          g?.trim()
        );
      break;
    }
    default:
      raw = script.greeting;
  }

  const spoken = raw ? humanizeVariableTokens(raw) : "";
  if (!spoken) return null;

  const maxChars = options?.maxChars ?? Number.POSITIVE_INFINITY;
  return spoken.length > maxChars ? spoken.slice(0, maxChars).trimEnd() : spoken;
}

/**
 * Unicode-aware capitalized name token (José, Íñigo, Grażyna …). The name
 * must be a capitalized word of 2+ letters NOT followed by another
 * capitalized word — "This is Acme Insurance" / "Soy Acme Seguros" is almost
 * certainly a business name, not an agent name, so we stay silent rather
 * than nag falsely.
 *
 * Apostrophes are allowed inside a name (O'Brien) but a trailing possessive
 * is not an introduction: "You've reached Mike's office" / "This is Gail's
 * assistant" name the office or the assistant, not the agent. The token must
 * be maximal — it cannot be followed by another name character — so
 * backtracking can never shorten "Mike's" to "Mike" or "Anne-Marie's" to
 * "Anne"; the maximal token is then rejected if it ends in `'`, `'s`, or a
 * dangling hyphen ("This is Gail- how can I help").
 */
const INTRO_NAME = "(\\p{Lu}[\\p{L}'’-]+)(?![\\p{L}'’-])(?<!['’-]|['’][sS])(?!\\s+\\p{Lu})";

/**
 * Conservative "the agent introduces itself as X" patterns for the locales
 * whose greetings use capitalized names (en/es/pt/fr/de). Japanese is
 * intentionally not covered — there is no capitalization signal to key on,
 * and a missed nudge is fine while a false one is not.
 */
const INTRO_NAME_PATTERNS: readonly RegExp[] = [
  // English
  new RegExp(`\\b[Tt]his is ${INTRO_NAME}`, "u"),
  new RegExp(`\\b[Mm]y name is ${INTRO_NAME}`, "u"),
  new RegExp(`\\bI(?:['’]m| am) ${INTRO_NAME}`, "u"),
  new RegExp(`\\b[Yy]ou(?:['’]ve| have) reached ${INTRO_NAME}`, "u"),
  // Spanish
  new RegExp(`\\b[Mm]e llamo ${INTRO_NAME}`, "u"),
  new RegExp(`\\b[Mm]i nombre es ${INTRO_NAME}`, "u"),
  new RegExp(`\\b[Ss]oy ${INTRO_NAME}`, "u"),
  new RegExp(`\\b[Ll]e atiende ${INTRO_NAME}`, "u"),
  // Portuguese (names commonly take an article: "sou a Maria")
  new RegExp(`\\b[Mm]e chamo ${INTRO_NAME}`, "u"),
  new RegExp(`\\b[Mm]eu nome é ${INTRO_NAME}`, "u"),
  new RegExp(`\\b[Ss]ou (?:[ao] )?${INTRO_NAME}`, "u"),
  new RegExp(`\\b[Aa]qui é (?:[ao] )?${INTRO_NAME}`, "u"),
  // French
  new RegExp(`\\b[Jj]e m['’]appelle ${INTRO_NAME}`, "u"),
  // German ("heiße Sie … willkommen" is "I welcome you", not a name)
  new RegExp(`\\b[Ii]ch (?:heiße|heisse) (?!Sie\\b)${INTRO_NAME}`, "u"),
  new RegExp(`\\b[Mm]ein Name ist ${INTRO_NAME}`, "u"),
];

/**
 * Remove variable tokens entirely (with a non-word marker so surrounding
 * words don't join up). Detection must never fire on a token's display label
 * — `this is @[Agent Name](agent_name)` is already correct by construction.
 */
function stripVariableTokens(text: string): string {
  return text.replace(MENTION_TOKEN_REGEX, " § ").replace(BRACE_TOKEN_REGEX, " § ");
}

/** The greeting texts a caller could actually hear under the active mode. */
function activeGreetingTexts(script: GreetingSource): string[] {
  const mode: GreetingMode = script.greetingMode ?? "static";
  switch (mode) {
    case "prompt":
      return [];
    case "random":
      return (script.greetingVariants ?? []).filter((v): v is string => !!v?.trim());
    case "time_specific":
      return [script.greetingMorning, script.greetingAfternoon, script.greetingNight].filter(
        (g): g is string => !!g?.trim()
      );
    default:
      return script.greeting?.trim() ? [script.greeting] : [];
  }
}

/**
 * Detect a greeting that introduces the agent by a name other than
 * `agentName` (the greeting is free text — nothing substitutes the agent
 * name at call time, so callers genuinely hear whatever was typed).
 *
 * Returns the mismatching spoken name, or null when there's nothing to
 * report: no agent name, prompt-mode greeting, no recognizable
 * introduction, or the introduced name already matches (case-insensitive).
 * Deliberately conservative — a missed nudge is fine, a false one is not.
 */
export function findGreetingNameMismatch(
  script: GreetingSource,
  agentName: string | undefined
): { spokenName: string } | null {
  const name = agentName?.trim();
  if (!name) return null;

  for (const text of activeGreetingTexts(script)) {
    const stripped = stripVariableTokens(text);
    for (const pattern of INTRO_NAME_PATTERNS) {
      const spokenName = pattern.exec(stripped)?.[1];
      if (spokenName && spokenName.toLowerCase() !== name.toLowerCase()) {
        return { spokenName };
      }
    }
  }
  return null;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Replace whole-word occurrences of `oldName` with `newName` across the
 * greeting fields of the active mode only. Mention tokens are left intact so
 * a rename can't corrupt `@[Display](id)` variables.
 */
export function replaceGreetingName<T extends GreetingSource>(
  script: T,
  oldName: string,
  newName: string
): T {
  const trimmedOld = oldName.trim();
  const trimmedNew = newName.trim();
  if (!trimmedOld || !trimmedNew) return script;

  // One combined pattern: mention tokens win the alternation, so the name is
  // only replaced outside of them. Letter lookarounds instead of \b — ASCII
  // \b fails at accented boundaries ("José" would never match).
  const combined = new RegExp(
    `${MENTION_TOKEN_REGEX.source}|(?<!\\p{L})${escapeRegExp(trimmedOld)}(?!\\p{L})`,
    "gu"
  );
  const sub = (text: string | undefined) =>
    text === undefined
      ? undefined
      : text.replace(combined, (match) => (match.startsWith("@[") ? match : trimmedNew));

  const mode: GreetingMode = script.greetingMode ?? "static";
  switch (mode) {
    case "prompt":
      return script;
    case "random":
      return {
        ...script,
        greetingVariants: script.greetingVariants?.map((v) => sub(v) ?? v),
      };
    case "time_specific":
      return {
        ...script,
        greetingMorning: sub(script.greetingMorning),
        greetingAfternoon: sub(script.greetingAfternoon),
        greetingNight: sub(script.greetingNight),
      };
    default:
      return { ...script, greeting: sub(script.greeting) };
  }
}

/**
 * Sanitize greeting fields at the save boundary.
 *
 * Keeps only the fields that belong to the active `greetingMode` and clears
 * the rest, so the persisted script carries a single coherent greeting
 * configuration instead of leftovers from earlier mode choices.
 *
 * - "static": keeps `greeting`; clears prompt/variants/time-specific fields.
 * - "prompt": keeps `greetingPrompt`; clears the rest (`greeting` → "").
 * - "random": keeps `greetingVariants`; clears the rest (`greeting` → "").
 * - "time_specific": keeps morning/afternoon/night + their start times; clears
 *   the rest (`greeting` → "").
 *
 * The static `greeting` field is set to `""` rather than left undefined when
 * the mode isn't "static" because the API treats it as a plain string column.
 */
export function sanitizeGreetingFields<T extends GreetingSource>(script: T): T {
  const mode: GreetingMode = script.greetingMode ?? "static";
  const isStatic = mode === "static";
  const isTimeSpecific = mode === "time_specific";

  return {
    ...script,
    greeting: isStatic ? (script.greeting ?? "") : "",
    greetingPrompt: mode === "prompt" ? script.greetingPrompt : undefined,
    greetingVariants: mode === "random" ? script.greetingVariants : undefined,
    greetingMorning: isTimeSpecific ? script.greetingMorning : undefined,
    greetingAfternoon: isTimeSpecific ? script.greetingAfternoon : undefined,
    greetingNight: isTimeSpecific ? script.greetingNight : undefined,
    greetingMorningStart: isTimeSpecific ? script.greetingMorningStart : undefined,
    greetingAfternoonStart: isTimeSpecific ? script.greetingAfternoonStart : undefined,
    greetingNightStart: isTimeSpecific ? script.greetingNightStart : undefined,
  };
}
